/**
 * "Don't Teach (in Stem or Options)" (BCE's internal code "Z") is the one
 * SOCRATES criterion that's a judgment call rather than a defect, so it gets
 * an orange "?" presentation instead of the red X plus guidance asking the
 * teacher to decide — worded differently depending on whether the teaching
 * landed in the stem or in the options (Toni May / psychometrician request).
 *
 * Shared by the item card and the Edit-item modal so the two surfaces can't
 * drift on which half fired or on the copy itself.
 */

/** SOCRATES' flag key for the criterion (its label covers stem AND options). */
export const DONT_TEACH_KEY = "do_not_teach_in_stem";

// The criterion KEEPS its document name — "Don't Teach (in Stem or Options)"
// in the criteria grid, code and data (Toni, 2026-09-26: display-label change
// only). The Issues found heading, however, names whichever half actually
// carries the teaching, so the teacher knows where to look:
export const DONT_TEACH_STEM_HEADING = "Don't Teach in Stem";
export const DONT_TEACH_OPTIONS_HEADING = "Don't Teach in Options";
export const DONT_TEACH_BOTH_HEADING = "Don't Teach in Stem and Options";

export const DONT_TEACH_STEM_GUIDANCE =
  "Stem contains information that may not be necessary to answer the question. " +
  "If removing the text would make the question unanswerable or ambiguous, keep it. " +
  "If not, edit or regenerate.";

export const DONT_TEACH_OPTIONS_GUIDANCE =
  "Option(s) contain information that may not be necessary to answer the question. " +
  "Review and determine if it should be kept, edited, or regenerated.";

// BCE emits this criterion's detail as "Z: Stem ..." / "Z: Option(s) ...",
// either joined with " | " in one entry or as separate `issues` entries. The
// "Z:" tag is the criterion's own provenance marker; the half detection keys
// off it so only criterion 6 findings are folded in. The rolled-up
// teaching-in-options path (BCE's DO_NOT_TEACH_IN_OPTIONS check, excluded from
// the 16-criterion framework) is emitted as "AC: Option(s) ..." and also
// belongs to this criterion. No other spelling is treated as Don't Teach,
// however much the text reads like teaching content: a foreign criterion's
// finding such as "Options describe the same concept in redundant wording"
// begins with "Options" and contains "describe", but is not this criterion and
// must not be swallowed (ARC-296).
const DONT_TEACH_STEM_RE = /^\s*(?:[A-Z]{1,3}:\s*)?Stem\b/i;
const DONT_TEACH_OPTIONS_RE = /^\s*(?:[A-Z]{1,3}:\s*)?(?:Option\(\s*s\s*\)|Options\b)/i;

const splitSegments = (raw: string): string[] =>
  raw.split(/\s*\|\s*/).map((s) => s.trim()).filter((s) => s.length > 0);

const scopedToHalf = (seg: string, halfRe: RegExp): boolean => {
  if (!halfRe.test(seg)) return false;
  return /^\s*(?:Z|AC):/i.test(seg);
};

// ── Settled Don't Teach messages (ARC-298 / ARC-299) ─────────────────────────
//
// The settled wording omits the leading "Stem"/"Option(s)" marker blocks hang
// today's sort on: both halves phrase the finding as prose ("Check whether the
// question works without ..."). It carries this criterion's own "Z:" tag, so
// scoping to criterion 6 is the same tag-only gate. Sorting then keys off the
// options-naming form: a settled message that names options as `Option B: "..."`
// is an options finding whose letters are exactly those named; every other
// settled message is a stem finding.

/** The split option-name form a settled options finding uses ("Option B: ...",
 *  "Option D: ..."). Capital letters only, so "option below" in the prose must
 *  never be read as an affected option token. */
const SETTLED_OPTIONS_NAME_RE = /\bOption\s+([A-Z])\s*:/g;

const settledOptionLetters = (seg: string): string[] => {
  const re = new RegExp(SETTLED_OPTIONS_NAME_RE.source, "g");
  const out: string[] = [];
  for (const m of seg.matchAll(re)) out.push(m[1]);
  return [...new Set(out)];
};

/** True when a segment is one of this criterion's own messages. The settled
 *  forms carry no "Stem"/"Option(s)" marker to sort on, so only the
 *  criterion's own "Z:" tag scopes them: another criterion's finding that
 *  happens to read like teaching content ("Definitions belong in the stem...")
 *  must never be swallowed by the settled path (the tag is the one reliable
 *  ownership signal, and the settled wording always arrives with it). */
const isCriterion6Message = (seg: string): boolean =>
  /^\s*Z:/i.test(seg);

const isSettledOptionsSegment = (seg: string): boolean =>
  isCriterion6Message(seg) &&
  !scopedToHalf(seg, DONT_TEACH_STEM_RE) &&
  !scopedToHalf(seg, DONT_TEACH_OPTIONS_RE) &&
  settledOptionLetters(seg).length > 0;

const isSettledStemSegment = (seg: string): boolean =>
  isCriterion6Message(seg) &&
  !scopedToHalf(seg, DONT_TEACH_STEM_RE) &&
  !scopedToHalf(seg, DONT_TEACH_OPTIONS_RE) &&
  settledOptionLetters(seg).length === 0;

const isSettledOptionsMessage = (raw: string): boolean =>
  splitSegments(raw).some(isSettledOptionsSegment);

const isSettledStemMessage = (raw: string): boolean =>
  splitSegments(raw).some(isSettledStemSegment);

const isStemMessage = (raw: string): boolean =>
  splitSegments(raw).some((s) => scopedToHalf(s, DONT_TEACH_STEM_RE) || isSettledStemSegment(s));

const isOptionsMessage = (raw: string): boolean =>
  splitSegments(raw).some((s) => scopedToHalf(s, DONT_TEACH_OPTIONS_RE) || isSettledOptionsSegment(s));

/**
 * True for a raw issue message that belongs to this criterion. Callers use it
 * to keep the message out of their generic issue list — the tailored guidance
 * below already covers it, and leaving it in renders the same finding twice.
 */
export function isDontTeachIssue(raw: string): boolean {
  return isStemMessage(raw) || isOptionsMessage(raw);
}

export interface DontTeachGroup {
  /** Affected option letters in displayed (post-sort) order. Empty when the
   *  raw messages named no letters — the generic options copy is shown. */
  optionLetters: string[];
  /** The advisory copy rendered for this group. For the canonical
   *  teaching-content family this is the templated lettered advisory; for a
   *  distinct finding it is the specific, tidied finding text so nothing the
   *  validator said is lost. */
  message: string;
}

export interface DontTeachGuidance {
  flagged: boolean;
  /** The Issues found heading for this verdict, naming the half (or both
   *  halves) the teaching landed in — never the generic
   *  "in Stem or Options" label on this surface (Toni, 2026-09-26). */
  heading: string;
  showStem: boolean;
  showOptions: boolean;
  /** The analyzer's stem-scoped Don't Teach messages, their leading column
   *  tags removed, in first-seen order, the words the writer sees under the
   *  stem heading. Empty when the half only renders the front-end guidance
   *  fallback (flag fired with no analyzer detail). */
  stemMessages: string[];
  /** One entry per distinct affected-option letter set found in the raw
   *  options-scoped messages, in first-seen order. Messages that repeat the
   *  same finding — same criterion, same affected options, same semantic
   *  finding — collapse into one group so the teacher sees one advisory
   *  paragraph instead of N copies. Distinct findings stay separate even when
   *  they affect the same options, so genuinely different problems aren't
   *  silently merged. */
  optionGroups: DontTeachGroup[];
}

/** The options marker plus anything before it within the segment (tag,
 *  whitespace), consumed up to the letter list that BCE puts right after it. */
const OPTIONS_LETTERS_AFTER_RE = /^\s*(?:[A-Za-z]+\s*:\s*)?(?:Option\(\s*s\s*\)|Options)\s*/i;

/** The canonical teaching-content finding family: items where the options
 *  themselves teach (tacked-on definitions, "terms only", explaining content
 *  instead of testing, etc.). Different remediation passes phrase the same
 *  underlying finding differently ("carry tacked-on definitions..." /
 *  "contain teaching/definitions..." / "explain what X is instead of testing
 *  it"), so they all map to one semantic key and collapse. Any finding that
 *  isn't this family keeps its own key (e.g. "reveals the correct answer
 *  through grammatical cues") and never merges with it or with each other. */
const TEACHING_FINDING_RE =
  /teach|\btacked-?on\b|\b(?:definition|definitions)\b|\b(?:describe|describes|described|describing|description|descriptions)\b|\b(?:define|defines|defined|defining)\b|\b(?:explain|explains|explained|explaining|explanation|explanatory)\b|\bterms?\s+only\b|\bmove the teaching out\b|\bput definitions in stem\b|\binstead of\s+(?:testing|asking)\b|\brather than\s+(?:testing|asking)\b/i;

interface ParsedOptionsFinding {
  /** Affected option letters, matching the displayed order, sorted. */
  letters: string[];
  /** Semantic discriminator. "teaching" for the canonical teaching-content
   *  family; "settled" for the word-for-word settled options finding; a
   *  normalized copy of the finding text otherwise (so distinct non-teaching
   *  findings never collide just for sharing letters). */
  kind: string;
  /** The advice/prose after the option letters (tag + marker stripped). Empty
   *  for the settled wording, which is carried whole in `full`. */
  body: string;
  /** The whole analyzer message, its leading column tag removed. Marker-form
   *  findings rebuild their display from lead + letters + body; the settled
   *  options finding displays this text word for word. */
  full: string;
  /** The leading options marker the analyzer wrote ("Option(s) " / "Options ")
   *  so the displayed text keeps its original lead word. Empty for settled. */
  lead: string;
}

/**
 * Split a raw issues entry into its per-segment options findings. A joined
 * "Z: Stem ... | Z: Option(s) ..." entry contributes its options segment here
 * (the stem segment is handled separately by the stem guidance).
 */
function parseOptionsFindings(raw: string): ParsedOptionsFinding[] {
  const out: ParsedOptionsFinding[] = [];
  for (const seg of splitSegments(raw)) {
    // Settled options wording has no leading "Option(s)" marker; it names the
    // affected options inline ("Option B: ..."), and displays word for word.
    if (isSettledOptionsSegment(seg)) {
      const letters = settledOptionLetters(seg).sort();
      out.push({
        letters,
        kind: "settled",
        body: "",
        full: stripColumnTag(seg),
        lead: "",
      });
      continue;
    }
    if (!isOptionsMessage(seg)) continue;
    const marker = OPTIONS_LETTERS_AFTER_RE.exec(seg);
    if (!marker) continue;
    const lead = stripColumnTag(marker[0]);
    let rest = seg.slice(marker[0].length);
    const rawLetters: string[] = [];
    while (rest.length > 0) {
      const and = rest.match(/^and\b/i);
      if (and) {
        rest = rest.slice(and[0].length);
        continue;
      }
      const sep = rest.match(/^[\s,]+/);
      if (sep) {
        rest = rest.slice(sep[0].length);
        continue;
      }
      // An option letter is a STANDALONE single-capital token. "B" in
      // "B, D ..." is one; the "E" of a capitalized prose word like
      // "Options Explain ..." must not be — a word boundary after the
      // capital only exists when the capital isn't followed by lowercase
      // (i.e. it isn't part of a word). Without this, no-letter emissions
      // starting with a capital would fabricate an affected option.
      const letter = rest.match(/^[A-Z]\b/);
      if (!letter) break; // Prose begins ("carry tacked-on..." etc.)
      rawLetters.push(letter[0]);
      rest = rest.slice(1);
    }
    const body = rest.trim();
    out.push({
      letters: [...new Set(rawLetters)].sort(),
      kind: TEACHING_FINDING_RE.test(body)
        ? "teaching"
        : `text:${body.toLowerCase().replace(/\s+/g, " ")}`,
      body,
      full: stripColumnTag(seg),
      lead,
    });
  }
  return out;
}

/** Strip the analyzer's column tag ("Z:", "AC:", a snake_case criterion prefix)
 *  from the front of a message so only the finding's own words remain. */
const stripColumnTag = (seg: string): string =>
  seg.replace(/^\s*(?:[A-Z]{1,3}|[A-Za-z]+_[A-Za-z0-9_]*):\s*/, "");

function toGroup(f: ParsedOptionsFinding): DontTeachGroup {
  // Settled options wording displays word for word, its affected options
  // already named inside the message ("Option B: ... Option D: ...").
  if (f.kind === "settled") {
    return { optionLetters: f.letters, message: f.full.trim() };
  }
  // Today's messages show the analyzer's own finding text (letters rebuilt in
  // display order), never the front-end's lettered advisory. Nothing the
  // validator wrote is dropped: the lead marker, the impacted options and the
  // advice all survive.
  const body = f.body;
  const letterPart = f.letters.length > 0 ? `${f.letters.join(", ")} ` : "";
  const message = `${f.lead}${letterPart}${body}`.trim();
  return { optionLetters: f.letters, message };
}

/**
 * Groups options-scoped messages by (affected-option letter set, semantic
 * finding). Letters are the analyzer's letters, which already match the
 * displayed order — Waggle never re-sorts answer options, so no remapping is
 * needed. Messages naming the same options for the same semantic finding
 * collapse into one group; messages that are genuinely different findings keep
 * their own groups even with identical letters. A bare "Option(s)..." message
 * (no letters) claims the same thing as a lettered one of the same finding
 * without narrowing it, so when exactly one lettered group of that finding
 * exists it absorbs the bare message — one paragraph, letters preserved. With
 * several lettered groups a bare message can't be attributed to one of them,
 * so it stands alone so no finding is dropped.
 */
function groupOptionMessages(messages: string[]): DontTeachGroup[] {
  const groups = new Map<string, ParsedOptionsFinding>();
  for (const raw of messages) {
    for (const parsed of parseOptionsFindings(raw)) {
      const key = `${parsed.kind}\u0000${parsed.letters.join("")}`;
      if (!groups.has(key)) groups.set(key, parsed);
    }
  }

  const byKind = new Map<string, ParsedOptionsFinding[]>();
  for (const f of groups.values()) {
    const list = byKind.get(f.kind) ?? [];
    list.push(f);
    byKind.set(f.kind, list);
  }

  const result: DontTeachGroup[] = [];
  for (const list of byKind.values()) {
    const lettered = list.filter((f) => f.letters.length > 0);
    const bare = list.filter((f) => f.letters.length === 0);
    for (const f of lettered) result.push(toGroup(f));
    if (bare.length > 0 && lettered.length !== 1) {
      // No single lettered group to attribute the bare message to (either zero
      // or several distinct lettered findings) — keep it so no finding drops.
      for (const f of bare) result.push(toGroup(f));
    }
    // bare.length > 0 && lettered.length === 1 → absorbed above.
  }
  return result;
}

/** The options advisory copy for a group (see DontTeachGroup.message). */
export function dontTeachOptionsMessage(group: DontTeachGroup): string {
  return group.message;
}

/**
 * Which guidance paragraphs to render for a scored cycle. When the messages
 * name both halves — or name neither, including when BCE sends no detail at
 * all — both halves are shown rather than guessing wrong and pointing the
 * teacher at the wrong part of the item.
 *
 * `qualityFeedback` is the advisory array for the displayed cycle (see
 * feedbackForCycle in utils/socratesScore). This criterion's own entries —
 * when the finding is carried there rather than in the raw issues list — also
 * count toward which half fired, so the stem/options heading and body stay
 * correct no matter which channel the analyzer used.
 */
export function dontTeachGuidance(
  flags: Record<string, number>,
  issues: string[],
  qualityFeedback?: ReadonlyArray<{ criterion: string; description: string }>,
): DontTeachGuidance {
  const flagged = flags[DONT_TEACH_KEY] === 1;
  const feedbackMessages = (qualityFeedback ?? [])
    .filter((f) => (f.criterion ?? "") === DONT_TEACH_KEY)
    .map((f) => (f.description ?? "").trim())
    .filter((s) => s.length > 0);
  // Fold the feedback descriptions into the message pool so the stem/options
  // split still works when the finding lives only in quality_feedback.
  const messages = [...issues, ...feedbackMessages];
  const stemFired = flagged && messages.some(isStemMessage);
  const optionsFired = flagged && messages.some(isOptionsMessage);
  const bothFired = stemFired && optionsFired;
  // Flagged with no scoped message at all: don't guess which half — both
  // halves keep showing, exactly the pre-existing no-detail behaviour.
  const neitherFired = flagged && !stemFired && !optionsFired;
  // The analyzer's own stem-scoped messages, column tags removed, in
  // first-seen order (a duplicate wording from issues + quality_feedback that
  // references the same finding is not shown twice).
  const stemMessages: string[] = [];
  if (stemFired) {
    const seen = new Set<string>();
    for (const raw of messages) {
      for (const seg of splitSegments(raw)) {
        const isStemSeg =
          scopedToHalf(seg, DONT_TEACH_STEM_RE) || isSettledStemSegment(seg);
        if (!isStemSeg) continue;
        const text = stripColumnTag(seg).trim();
        if (text && !seen.has(text)) {
          seen.add(text);
          stemMessages.push(text);
        }
      }
    }
  }
  return {
    flagged,
    heading: bothFired || neitherFired
      ? DONT_TEACH_BOTH_HEADING
      : stemFired
        ? DONT_TEACH_STEM_HEADING
        : DONT_TEACH_OPTIONS_HEADING,
    showStem: flagged && (stemFired || neitherFired),
    showOptions: flagged && (optionsFired || neitherFired),
    stemMessages,
    optionGroups: optionsFired ? groupOptionMessages(messages) : [],
  };
}

/**
 * The per-item rewrite BCE attaches to a Don't Teach verdict: the analyzer
 * emits severity=advisory plus a suggestion (e.g. a rewritten stem) in
 * quality_feedback, scoped to the Don't Teach criterion. Returns the first
 * non-empty advisory suggestion for THIS criterion, or null when none is
 * present so the surfaces render the static guidance alone. The `criterion`
 * filter is essential — other criteria can carry their own advisory entries
 * in the same array, and surfacing one of those under Don't Teach would be
 * the same false attribution ARC-66 exists to eliminate.
 *
 * The live API delivers `suggestion: null` on most rows, so a null or blank
 * suggestion in an early entry is skipped and scanning continues for a later
 * non-empty one instead of aborting the search (ARC-302).
 */
export function dontTeachAdvisorySuggestion(
  qualityFeedback?: Array<{ severity: string; criterion: string; suggestion?: string | null }>,
): string | null {
  if (!qualityFeedback || qualityFeedback.length === 0) return null;
  for (const f of qualityFeedback) {
    if (
      (f.criterion ?? "") === DONT_TEACH_KEY &&
      f.severity === "advisory"
    ) {
      const suggestion = f.suggestion?.trim();
      if (suggestion) return suggestion;
    }
  }
  return null;
}
